import { get } from 'lodash';
import { useState, useEffect, useCallback } from 'react';
import { externalInstance } from "../api/axiosInstance";

// ============ Types ============

interface FieldMapping {
  path?: string;
  transform?: string;
  template?: string;
  default?: any;
}

interface FlattenConfig {
  arrayPath: string;
  keyField: string;
  valueField: string;
}

interface ResponseMapping {
  dataPath?: string;
  fields: Record<string, string | FieldMapping>;
  flatten?: FlattenConfig;
}

interface ApiConfig {
  endpoint: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: any;
  path?: string;
  queryParams?: Record<string, string | number | boolean>;
  responseMapping: ResponseMapping;
  refreshInterval?: number;
}

interface Widget {
  id: string;
  type: string;
  title: string;
  dataKey: string | string[];
  xKey?: string;
  apiConfig?: ApiConfig;
  data?: any[];
}

// ============ Transform Registry ============

type TransformFn = (value: any, ...args: string[]) => any;

const transforms: Record<string, TransformFn> = {
  // Number transforms
  percentage: (v) => (typeof v === 'number' ? v * 100 : v),
  round: (v, decimals = '2') => {
    const d = parseInt(decimals);
    const factor = Math.pow(10, d);
    return typeof v === 'number' ? Math.round(v * factor) / factor : v;
  },
  abs: (v) => (typeof v === 'number' ? Math.abs(v) : v),
  negate: (v) => (typeof v === 'number' ? -v : v),
  
  // String transforms
  uppercase: (v) => (typeof v === 'string' ? v.toUpperCase() : v),
  lowercase: (v) => (typeof v === 'string' ? v.toLowerCase() : v),
  capitalize: (v) => (typeof v === 'string' ? v.charAt(0).toUpperCase() + v.slice(1) : v),
  trim: (v) => (typeof v === 'string' ? v.trim() : v),
  
  // Date transforms (using basic formatting, swap for dayjs if available)
  date: (v, format = 'YYYY-MM-DD') => {
    if (!v) return v;
    const d = new Date(v);
    // Basic format support - extend as needed
    return format
      .replace('YYYY', d.getFullYear().toString())
      .replace('MM', (d.getMonth() + 1).toString().padStart(2, '0'))
      .replace('DD', d.getDate().toString().padStart(2, '0'))
      .replace('HH', d.getHours().toString().padStart(2, '0'))
      .replace('mm', d.getMinutes().toString().padStart(2, '0'))
      .replace('ss', d.getSeconds().toString().padStart(2, '0'));
  },
  timestamp: (v) => (v ? new Date(v).getTime() : v),
  
  // Boolean transforms
  boolean: (v) => Boolean(v),
  not: (v) => !v,
  
  // JSON transforms
  parse: (v) => {
    try {
      return JSON.parse(v);
    } catch {
      return v;
    }
  },
  stringify: (v) => JSON.stringify(v),
  
  // Array transforms
  first: (v) => (Array.isArray(v) ? v[0] : v),
  last: (v) => (Array.isArray(v) ? v[v.length - 1] : v),
  count: (v) => (Array.isArray(v) ? v.length : v),
  join: (v, separator = ', ') => (Array.isArray(v) ? v.join(separator) : v),
  
  // Null handling
  nullTo: (v, fallback = '') => (v == null ? fallback : v),
  emptyTo: (v, fallback = '') => (v === '' || v == null ? fallback : v),
};

// ============ Core Functions ============

/**
 * Parse transform string like "round:2" or "date:YYYY-MM-DD"
 */
function parseTransform(transformStr: string): { name: string; args: string[] } {
  const [name, ...args] = transformStr.split(':');
  return { name, args };
}

/**
 * Apply a single transform to a value
 */
function applyTransform(value: any, transformStr: string): any {
  const { name, args } = parseTransform(transformStr);
  const transformFn = transforms[name];
  
  if (!transformFn) {
    console.warn(`Unknown transform: ${name}`);
    return value;
  }
  
  return transformFn(value, ...args);
}

/**
 * Apply multiple transforms in sequence (pipe syntax: "round:2|percentage")
 */
function applyTransforms(value: any, transformStr: string): any {
  const transformList = transformStr.split('|').map((t) => t.trim());
  return transformList.reduce((v, t) => applyTransform(v, t), value);
}

/**
 * Resolve a template string like "{firstName} {lastName}"
 */
function resolveTemplate(template: string, item: any): string {
  return template.replace(/\{([^}]+)\}/g, (_, path) => {
    const value = get(item, path.trim());
    return value != null ? String(value) : '';
  });
}

/**
 * Extract a single field value from an item based on field config
 */
function extractFieldValue(item: any, fieldConfig: string | FieldMapping): any {
  // Simple string path
  if (typeof fieldConfig === 'string') {
    return get(item, fieldConfig);
  }
  
  // Template-based field
  if (fieldConfig.template) {
    return resolveTemplate(fieldConfig.template, item);
  }
  
  // Path-based with optional transform
  let value = fieldConfig.path ? get(item, fieldConfig.path) : undefined;
  
  // Apply default if value is null/undefined
  if (value == null && fieldConfig.default !== undefined) {
    value = fieldConfig.default;
  }
  
  // Apply transform if specified
  if (fieldConfig.transform && value != null) {
    value = applyTransforms(value, fieldConfig.transform);
  }
  
  return value;
}

/**
 * Flatten nested arrays into object properties
 * Converts: { metrics: [{name: "cv_auc", value: 0.56}, ...] }
 * To: { cv_auc: 0.56, ... }
 */
function flattenItem(item: any, flattenConfig: FlattenConfig): any {
  const { arrayPath, keyField, valueField } = flattenConfig;
  const array = get(item, arrayPath);
  
  if (!Array.isArray(array)) {
    return item;
  }
  
  const flattened = { ...item };
  
  // Remove the original array
  const pathParts = arrayPath.split('.');
  if (pathParts.length === 1) {
    delete flattened[arrayPath];
  }
  
  // Add flattened key-value pairs
  for (const entry of array) {
    const key = get(entry, keyField);
    const value = get(entry, valueField);
    if (key != null) {
      flattened[key] = value;
    }
  }
  
  return flattened;
}

/**
 * Map a single raw item to the widget's expected shape
 */
export function mapItem(item: any, mapping: ResponseMapping): any {
  // Apply flattening first if configured
  let processedItem = item;
  if (mapping.flatten) {
    processedItem = flattenItem(item, mapping.flatten);
  }
  
  // Map fields
  const result: Record<string, any> = {};
  
  for (const [targetKey, fieldConfig] of Object.entries(mapping.fields)) {
    result[targetKey] = extractFieldValue(processedItem, fieldConfig);
  }
  
  return result;
}

/**
 * Build URL with query params
 */
function buildUrl(endpoint: string, path:string, queryParams?: Record<string, string | number | boolean>): string {
  let params = "";
  let separator ="";
  if (queryParams){
    params = Object.entries(queryParams)
    .map(([key, value]) => {
      const val = Array.isArray(value) ? value.join(',') : value;
      return `${encodeURIComponent(key)}=${val}`; // Don't encode the value
    })
    .join('&');
    separator = endpoint.includes('?') ? '&' : '?';
  }
  return `${endpoint}${path}${separator}${params}`;

}

// ============ Main Fetcher ============

export async function fetchWidgetData(apiConfig: ApiConfig): Promise<any[]> {
  const { 
    endpoint, 
    method = 'GET',
    queryParams, 
    body,
    headers = {},
    path ='',
  } = apiConfig;

  // Build URL with query params
  const url = buildUrl(endpoint, path, queryParams);

  let response;

  try {
    // Handle different HTTP methods
    switch (method.toUpperCase()) {
      case 'GET':
        response = await externalInstance.get(url, { headers });
        break;
      case 'POST':
        response = await externalInstance.post(url, body, { headers });
        break;
      case 'PUT':
        response = await externalInstance.put(url, body, { headers });
        break;
      case 'PATCH':
        response = await externalInstance.patch(url, body, { headers });
        break;
      case 'DELETE':
        response = await externalInstance.delete(url, { 
          headers,
          data: body // Some APIs accept body in DELETE
        });
        break;
      
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    const json = response.data;
    return json;

  } catch (error) {
    console.error(`Error fetching widget data from ${url}:`, error);
    throw error;
  }
}
// ============ Hook for React (optional) ============

interface UseWidgetDataOptions {
  enabled?: boolean;
  onError?: (error: Error) => void;
}

interface UseWidgetDataResult {
  data: any[] | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}



export function useWidgetData(
  widget: Widget,
  options: UseWidgetDataOptions = {}
): UseWidgetDataResult {
  const { enabled = true, onError } = options;
  
  const [data, setData] = useState<any[] | null>(widget.data ?? null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  const refetch = useCallback(async () => {
    if (!widget.apiConfig) {
      setData(widget.data ?? []);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await fetchWidgetData(widget.apiConfig);
      setData(result);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      onError?.(err);
    } finally {
      setIsLoading(false);
    }
  }, [widget, onError]);
  
  useEffect(() => {
    if (!enabled) return;
    
    refetch();
    
    // Set up polling if configured
    if (widget.apiConfig?.refreshInterval) {
      const interval = setInterval(refetch, widget.apiConfig.refreshInterval);
      return () => clearInterval(interval);
    }
  }, [enabled, refetch, widget.apiConfig?.refreshInterval]);
  
  return { data, isLoading, error, refetch };
}
